import React, { useEffect, useState } from "react";
import axios from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSync,
  FaSearch,
  FaTimes,
  FaPlus,
  FaArrowDown,
  FaArrowUp,
} from "react-icons/fa";

function MovimientoInventarioPage() {
  const [movimientos, setMovimientos] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [insumoFiltro, setInsumoFiltro] = useState("Todos");
  const [tipoFiltro, setTipoFiltro] = useState("Todos");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [busqueda, setBusqueda] = useState("");

  // Modal movimiento
  const [mostrarModal, setMostrarModal] = useState(false);
  const [tipoMovimientoForm, setTipoMovimientoForm] = useState("entrada"); // entrada | salida
  const [movimientoForm, setMovimientoForm] = useState({
    insumoId: "",
    cantidad: "",
    descripcion: "",
  });

  const tiposMovimiento = [
    "Todos",
    "ENTRADA_COMPRA",
    "ENTRADA_AJUSTE",
    "SALIDA_VENTA",
    "SALIDA_MERMA",
    "SALIDA_AJUSTE",
  ];

  useEffect(() => {
    cargarInsumos();
    cargarMovimientos();
  }, []);

  const cargarInsumos = async () => {
    try {
      const res = await axios.get("/insumos", { params: { activo: true } });
      setInsumos(res.data || []);
    } catch (error) {
      console.error("Error al cargar insumos:", error);
      toast.error("❌ Error al cargar insumos");
    }
  };

  const construirFechaISO = (fecha, finDia = false) => {
    if (!fecha) return null;
    return finDia ? `${fecha}T23:59:59` : `${fecha}T00:00:00`;
  };

  const cargarMovimientos = async () => {
    setLoading(true);
    try {
      const params = {};

      if (insumoFiltro !== "Todos") {
        params.insumoId = insumoFiltro;
      }

      if (tipoFiltro !== "Todos") {
        params.tipo = tipoFiltro;
      }

      const desdeISO = construirFechaISO(fechaDesde, false);
      const hastaISO = construirFechaISO(fechaHasta, true);
      if (desdeISO) params.desde = desdeISO;
      if (hastaISO) params.hasta = hastaISO;

      const res = await axios.get("/inventario/movimientos", { params });

      let data = res.data || [];

      // Filtro de texto sobre descripción / insumo / tipo
      if (busqueda.trim() !== "") {
        const q = busqueda.toLowerCase();
        data = data.filter((m) => {
          const desc = (m.descripcion || "").toLowerCase();
          const insumoNombre = (m.insumo?.nombre || "").toLowerCase();
          const tipo = (m.tipo || "").toLowerCase();
          return (
            desc.includes(q) || insumoNombre.includes(q) || tipo.includes(q)
          );
        });
      }

      setMovimientos(data);
    } catch (error) {
      console.error("Error al cargar movimientos:", error);
      toast.error("❌ Error al cargar movimientos");
    } finally {
      setLoading(false);
    }
  };

  const abrirModalMovimiento = (tipo) => {
    setTipoMovimientoForm(tipo); // "entrada" o "salida"
    setMovimientoForm({
      insumoId: "",
      cantidad: "",
      descripcion: "",
    });
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
  };

  const guardarMovimiento = async () => {
    if (!movimientoForm.insumoId || !movimientoForm.cantidad) {
      toast.warning("⚠️ Selecciona un insumo y una cantidad");
      return;
    }

    const cantidadNum = Number(movimientoForm.cantidad);
    if (isNaN(cantidadNum) || cantidadNum <= 0) {
      toast.warning("⚠️ La cantidad debe ser mayor a cero");
      return;
    }

    try {
      const params = {
        insumoId: movimientoForm.insumoId,
        cantidad: cantidadNum,
        descripcion: movimientoForm.descripcion || "",
      };

      if (tipoMovimientoForm === "entrada") {
        await axios.post("/inventario/entradas", null, { params });
        toast.success("✅ Entrada registrada correctamente");
      } else {
        await axios.post("/inventario/salidas", null, { params });
        toast.success("✅ Salida registrada correctamente");
      }

      setMostrarModal(false);
      cargarMovimientos();
      cargarInsumos(); // por si cambió stock/estado
    } catch (error) {
      console.error("Error al registrar movimiento:", error);
      toast.error("❌ No se pudo registrar el movimiento");
    }
  };

  const tipoEtiqueta = (tipo) => {
    switch (tipo) {
      case "ENTRADA_COMPRA":
        return { texto: "Entrada (compra)", clase: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" };
      case "ENTRADA_AJUSTE":
        return { texto: "Entrada (ajuste)", clase: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" };
      case "SALIDA_VENTA":
        return { texto: "Salida venta", clase: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" };
      case "SALIDA_MERMA":
        return { texto: "Salida merma", clase: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" };
      case "SALIDA_AJUSTE":
        return { texto: "Salida ajuste", clase: "bg-slate-100 text-slate-700 dark:bg-slate-900/60 dark:text-slate-300" };
      default:
        return { texto: tipo || "N/A", clase: "bg-slate-100 text-slate-700 dark:bg-slate-900/60 dark:text-slate-300" };
    }
  };

  const formTitulo =
    tipoMovimientoForm === "entrada" ? "➕ Registrar Entrada" : "➖ Registrar Salida";

  const formIcono =
    tipoMovimientoForm === "entrada" ? (
      <FaArrowDown className="text-emerald-400" />
    ) : (
      <FaArrowUp className="text-rose-400" />
    );

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 mt-3">
        {/* Encabezado */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300">
            Movimientos de Inventario
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => abrirModalMovimiento("entrada")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
            >
              <FaArrowDown />
              Nueva Entrada
            </button>

            <button
              onClick={() => abrirModalMovimiento("salida")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-white bg-rose-500 hover:bg-rose-600 transition-colors"
            >
              <FaArrowUp />
              Nueva Salida
            </button>

            <button
              onClick={cargarMovimientos}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600 text-xs sm:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <FaSync className="text-sky-500" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {/* Insumo */}
            <select
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
              value={insumoFiltro}
              onChange={(e) => setInsumoFiltro(e.target.value)}
              style={{ minWidth: "160px" }}
            >
              <option value="Todos">Insumo (todos)</option>
              {insumos.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.nombre}
                </option>
              ))}
            </select>

            {/* Tipo */}
            <select
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
              value={tipoFiltro}
              onChange={(e) => setTipoFiltro(e.target.value)}
              style={{ minWidth: "170px" }}
            >
              {tiposMovimiento.map((t, idx) => (
                <option key={idx} value={t}>
                  {t === "Todos" ? "Tipo (todos)" : t}
                </option>
              ))}
            </select>

            {/* Fecha desde */}
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />

            {/* Fecha hasta */}
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          {/* Buscador */}
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 overflow-hidden">
              <span className="pl-2 pr-1 text-slate-400">
                <FaSearch className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                placeholder="Buscar descripción / tipo / insumo..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="px-2 py-1.5 text-xs sm:text-sm bg-transparent text-slate-800 dark:text-slate-100 focus:outline-none"
                style={{ width: "220px" }}
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda("")}
                  className="px-2 py-1.5 text-xs text-slate-400 hover:text-red-400"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <button
              onClick={cargarMovimientos}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600 text-xs sm:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Aplicar filtros
            </button>
          </div>
        </div>

        {/* Contador */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            Mostrando {movimientos.length} movimiento
            {movimientos.length !== 1 ? "s" : ""}{" "}
            {insumoFiltro !== "Todos" && "para el insumo seleccionado"}{" "}
            {tipoFiltro !== "Todos" && `de tipo ${tipoFiltro}`}{" "}
            {fechaDesde && `desde ${fechaDesde}`}{" "}
            {fechaHasta && `hasta ${fechaHasta}`}{" "}
            {busqueda && `que coinciden con "${busqueda}"`}
          </span>
        </div>

        {/* Tabla */}
        <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[900px] md:min-w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-950/80">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    Fecha
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    Insumo
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Tipo
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Cantidad
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    Descripción
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Factura
                  </th>
                </tr>
              </thead>
              <tbody>
                {movimientos.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-4 text-center text-slate-500 dark:text-slate-400 text-sm"
                    >
                      {loading
                        ? "Cargando movimientos..."
                        : "No se encontraron movimientos"}
                    </td>
                  </tr>
                ) : (
                  movimientos.map((m) => {
                    const t = tipoEtiqueta(m.tipo);
                    const fecha =
                      m.fecha?.replace("T", " ").substring(0, 19) || "";
                    return (
                      <tr
                        key={m.id}
                        className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <td className="px-4 py-2 text-left align-middle">
                          {fecha}
                        </td>
                        <td className="px-4 py-2 text-left align-middle font-medium">
                          {m.insumo?.nombre || "-"}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${t.clase}`}
                          >
                            {t.texto}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          {m.cantidad}
                        </td>
                        <td className="px-4 py-2 text-left align-middle">
                          {m.descripcion || "-"}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          {m.factura?.id || "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal movimiento */}
      {mostrarModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:!bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:!bg-slate-800">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                {formIcono}
                {formTitulo}
              </h3>
              <button
                onClick={cerrarModal}
                className="p-1 rounded-full border border-slate-300 dark:border-slate-600
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-600 dark:!text-slate-200
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Insumo
                  </label>
                  <select
                    value={movimientoForm.insumoId}
                    onChange={(e) =>
                      setMovimientoForm((prev) => ({
                        ...prev,
                        insumoId: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  >
                    <option value="">Selecciona un insumo</option>
                    {insumos.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Cantidad
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={movimientoForm.cantidad}
                    onChange={(e) =>
                      setMovimientoForm((prev) => ({
                        ...prev,
                        cantidad: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    placeholder="Ej: 10, 2.5, 100..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Descripción (opcional)
                  </label>
                  <textarea
                    rows={3}
                    value={movimientoForm.descripcion}
                    onChange={(e) =>
                      setMovimientoForm((prev) => ({
                        ...prev,
                        descripcion: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none"
                    placeholder={
                      tipoMovimientoForm === "entrada"
                        ? "Ej: Compra proveedor X"
                        : "Ej: Merma, producto vencido..."
                    }
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:!bg-slate-800 rounded-b-2xl">
              <button
                onClick={cerrarModal}
                className="px-4 py-2 rounded-lg text-xs sm:text-sm font-medium
                          border border-slate-300 dark:border-slate-600
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-700 dark:!text-slate-200
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={guardarMovimiento}
                className="px-4 py-2 rounded-lg text-xs sm:text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
              >
                {tipoMovimientoForm === "entrada"
                  ? "✅ Registrar Entrada"
                  : "✅ Registrar Salida"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MovimientoInventarioPage;
